/**
 * DiffViewer — render unified diffu s barevnými řádky a Apply tlačítkem.
 *
 * Záměrně bez external library v1 — minimal CSS-only render.
 * V2 zvážit react-diff-viewer-continued nebo diff2html.
 */
import { Check, GitBranch, Loader2, Undo2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import type { Turn } from './DevstudioWorkspace';

interface Props {
  turn: Turn;
  onApply: (turnId: string) => void | Promise<void>;
  onRevert: (turnId: string) => void | Promise<void>;
}

function renderDiffLine(line: string, idx: number) {
  let cls = 'opacity-90';
  let prefix = ' ';
  if (line.startsWith('+++') || line.startsWith('---')) {
    cls = 'text-zinc-400 font-bold';
  } else if (line.startsWith('@@')) {
    cls = 'text-blue-400';
  } else if (line.startsWith('+')) {
    cls = 'bg-green-500/10 text-green-300';
    prefix = '+';
  } else if (line.startsWith('-')) {
    cls = 'bg-red-500/10 text-red-300';
    prefix = '-';
  }
  void prefix;
  return (
    <div key={idx} className={`px-2 ${cls}`}>
      {line || ' '}
    </div>
  );
}

export function DiffViewer({ turn, onApply, onRevert }: Props) {
  const { t } = useTranslation();
  if (!turn.diff_unified) return null;

  const lines = turn.diff_unified.split('\n');
  const stateLabel = {
    proposed: { txt: t('devstudio.diffProposed', 'navrženo'), color: 'text-yellow-400' },
    applying: { txt: t('devstudio.diffApplying', 'aplikuji…'), color: 'text-blue-400' },
    applied: { txt: t('devstudio.diffApplied', 'aplikováno'), color: 'text-green-400' },
    reverted: { txt: t('devstudio.diffReverted', 'revertováno'), color: 'text-zinc-500' },
    failed: { txt: t('devstudio.diffFailed', 'selhalo'), color: 'text-red-400' },
  }[turn.state];

  const deployBadge = turn.deploy_status === 'healthy'
    ? <span className="text-green-400">healthy</span>
    : turn.deploy_status === 'broken'
    ? <span className="text-red-400">broken</span>
    : turn.deploy_status === 'pending'
    ? <span className="text-blue-400 inline-flex items-center gap-1">
      <Loader2 size={10} className="animate-spin" /> deploy pending
    </span>
    : null;

  return (
    <div className="border border-zinc-700 rounded bg-zinc-950 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-2 py-1.5 bg-zinc-900 text-xs border-b border-zinc-800">
        <GitBranch size={12} />
        <span className={`font-mono ${stateLabel.color}`}>{stateLabel.txt}</span>
        {turn.commit_sha && (
          <span className="font-mono opacity-50">{turn.commit_sha.slice(0, 7)}</span>
        )}
        {deployBadge}
        <div className="ml-auto flex gap-1">
          {turn.state === 'proposed' && (
            <Button
              variant="primary"
              onClick={() => void onApply(turn.id)}
              title="Apply diff → commit + orchestrator rebuild"
            >
              <Check size={12} /> Apply
            </Button>
          )}
          {turn.state === 'applied' && (
            <Button
              variant="warning"
              onClick={() => void onRevert(turn.id)}
              title="Revert diff → git revert + rebuild"
            >
              <Undo2 size={12} /> Revert
            </Button>
          )}
          {turn.state === 'failed' && (
            <span className="text-xs text-red-400" title={turn.error_message ?? ''}>
              <X size={12} className="inline" /> {turn.error_message?.slice(0, 60)}
            </span>
          )}
        </div>
      </div>

      {/* Diff content */}
      <pre className="text-xs font-mono leading-snug max-h-[400px] overflow-auto">
        {lines.map((l, i) => renderDiffLine(l, i))}
      </pre>
    </div>
  );
}
